import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ActiveCallState, CallKind, CallLogEntry, EndedCallSummary, PendingCall } from '../models/call.model';
import { PollingService } from './polling.service';
import { identiteIntrouvable } from './identite-polling';

/**
 * MOB-000/MOB-010 — état d'appel global, survit à la navigation.
 *
 * legba-core/LiveKit ne connaît que la salle et le média une fois l'appel
 * accepté ; il n'a aucune notion d'« appel entrant » applicative (confirmé
 * en lisant son code — voir le plan `swirling-hatching-liskov.md`). Ce
 * service possède l'état qu'il ne fournit pas, et doit être `providedIn:
 * 'root'` + monté une seule fois dans `MainLayoutComponent` (jamais dans une
 * route qui se démonte à la navigation, ce qui casserait justement la
 * persistance demandée).
 */
@Injectable({ providedIn: 'root' })
export class CallService implements OnDestroy {
  private http = inject(HttpClient);
  private polling = inject(PollingService);
  private unregisterPolling: (() => void) | null = null;

  private readonly _activeCall = signal<ActiveCallState | null>(null);
  readonly activeCall = this._activeCall.asReadonly();

  /** Résumé du dernier appel raccroché — voir EndedCallSummary. Null tant qu'aucun appel n'a encore été terminé, ou une fois l'écran "Appel terminé" fermé. */
  private readonly _lastEndedCall = signal<EndedCallSummary | null>(null);
  readonly lastEndedCall = this._lastEndedCall.asReadonly();

  // Aucun double-tap sur les boutons d'appel (initier/répondre/refuser/
  // raccrocher) ne doit partir en double : un seul de ces appels HTTP à la
  // fois. Sans cette garde, un double clic sur "raccrocher" envoyait deux
  // POST .../end — le second, sur un appel déjà terminé, plantait en 500
  // (closeRoom() sur une salle déjà fermée côté serveur, jamais gardée par
  // un check d'idempotence).
  private busy = false;

  private get callsApiUrl(): string {
    return `${environment.apiUrls.users}/calls`;
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  /**
   * `<legba-call>` interroge son `token-endpoint` avec un `fetch()` brut, sans
   * aucun header [legba-core/packages/ui/src/composed/legba-call.ts] — donc
   * jamais le Bearer que l'intercepteur ajoute d'habitude. Ce POST-ci EST
   * authentifié normalement ; il renvoie un jeton signé court-terme (60s) à
   * embarquer directement dans l'URL passée à `<legba-call>` — voir
   * calls.controller.ts `mintCallAuth`/`callAuth`.
   */
  private async mintCallAuthUrl(callId: number): Promise<string> {
    const { token } = await firstValueFrom(
      this.http.post<{ token: string }>(`${this.callsApiUrl}/${callId}/call-auth`, {}),
    );
    return `${this.callsApiUrl}/call-auth?auth=${encodeURIComponent(token)}`;
  }

  /** Abonnement idempotent au tick partagé de `PollingService` — pas de nouvelle boucle. */
  startPolling() {
    if (this.unregisterPolling) return;
    this.unregisterPolling = this.polling.register(() => void this.pollPending());
  }

  stopPolling() {
    this.unregisterPolling?.();
    this.unregisterPolling = null;
  }

  /**
   * Ne jamais écraser un appel déjà en cours (sonnerie ou actif) par le poll
   * de nouveaux appels — mais le camp qui n'agit pas (l'émetteur pendant que
   * ça sonne côté appelé, ou n'importe quel côté une fois actif) n'a sinon
   * aucun moyen de savoir que l'autre a refusé/raccroché : `initiateCall`
   * passe l'émetteur en `active` immédiatement, et rien ne relisait plus
   * jamais l'état côté serveur ensuite. Même tick de `PollingService`, pas
   * une deuxième boucle.
   */
  private async pollPending(): Promise<void> {
    const current = this._activeCall();
    if (current) {
      await this.pollActiveCallStatus(current.callId);
      return;
    }
    try {
      const pending = await firstValueFrom(
        this.http.get<PendingCall[]>(`${this.callsApiUrl}/pending`),
      );
      const call = pending?.[0];
      if (call) {
        this._lastEndedCall.set(null); // un appel entrant efface le résumé du précédent, sinon les deux écrans se disputent l'affichage
        this._activeCall.set({
          callId: call.id,
          conversationId: call.conversationId,
          roomName: call.roomName,
          type: call.type,
          peerName: call.initiatorName,
          phase: 'ringing',
          invitedNames: [],
        });
      }
    } catch (erreur) {
      // Compte introuvable ou inactif côté serveur : reessayer toutes les 5 s n'y change rien (voir identite-polling).
      if (identiteIntrouvable(erreur)) {
        console.warn('[CallService] Compte introuvable côté serveur : le suivi des appels est arrêté jusqu\'à la prochaine connexion.');
        this.stopPolling();
      }
      /* sinon silencieux — même convention que ChatService.pollForUpdates */
    }
  }

  private async pollActiveCallStatus(callId: number): Promise<void> {
    try {
      const { status } = await firstValueFrom(
        this.http.get<{ status: string }>(`${this.callsApiUrl}/${callId}/status`),
      );
      if (status === 'declined' || status === 'ended' || status === 'missed') {
        this.finalizeEndedCall();
      }
    } catch {
      /* silencieux — même convention que pollPending */
    }
  }

  /**
   * Bascule vers l'écran "Appel terminé" UNIQUEMENT si l'appel avait
   * vraiment été décroché (`startedAt` posé) — un appel qui sonnait encore
   * quand l'autre camp a raccroché/refusé est un appel manqué, déjà
   * signalé via la carte dans la messagerie, pas un résumé de durée.
   */
  private finalizeEndedCall(): void {
    const call = this._activeCall();
    this._activeCall.set(null);
    if (!call || call.startedAt === undefined) return;
    this._lastEndedCall.set({
      conversationId: call.conversationId,
      type: call.type,
      peerName: call.peerName,
      invitedNames: call.invitedNames,
      startedAt: call.startedAt,
      endedAt: Date.now(),
    });
  }

  dismissEndedCall(): void {
    this._lastEndedCall.set(null);
  }

  async initiateCall(conversationId: number, type: CallKind, peerName: string): Promise<void> {
    if (this._activeCall() || this.busy) return; // un appel à la fois, jamais deux en vol
    this.busy = true;
    this._lastEndedCall.set(null); // un nouvel appel efface le résumé du précédent
    try {
      const result = await firstValueFrom(
        this.http.post<{ id: number; roomName: string }>(this.callsApiUrl, {
          conversationId,
          type,
        }),
      );
      this._activeCall.set({
        callId: result.id,
        conversationId,
        roomName: result.roomName,
        type,
        peerName,
        phase: 'active',
        startedAt: Date.now(),
        invitedNames: [],
      });
      const callAuthUrl = await this.mintCallAuthUrl(result.id);
      this._activeCall.update((c) => (c ? { ...c, callAuthUrl } : c));
    } finally {
      this.busy = false;
    }
  }

  async answer(options: { audioSeul?: boolean } = {}): Promise<void> {
    const call = this._activeCall();
    if (!call || this.busy) return;
    this.busy = true;
    try {
      await firstValueFrom(this.http.post(`${this.callsApiUrl}/${call.callId}/answer`, {}));
      this._activeCall.update((c) => (c ? { ...c, phase: 'active', startedAt: Date.now(), audioSeul: options.audioSeul === true } : c));
      const callAuthUrl = await this.mintCallAuthUrl(call.callId);
      this._activeCall.update((c) => (c ? { ...c, callAuthUrl } : c));
    } finally {
      this.busy = false;
    }
  }

  async decline(): Promise<void> {
    const call = this._activeCall();
    if (!call || this.busy) return;
    this.busy = true;
    try {
      await firstValueFrom(this.http.post(`${this.callsApiUrl}/${call.callId}/decline`, {}));
    } catch {
      // L'appel local disparaît quand même : la caissière a clairement
      // refusé, un échec réseau ne doit pas la laisser bloquée sur cet écran.
    } finally {
      this._activeCall.set(null);
      this.busy = false;
    }
  }

  async endCall(): Promise<void> {
    const call = this._activeCall();
    if (!call || this.busy) return;
    this.busy = true;
    try {
      await firstValueFrom(this.http.post(`${this.callsApiUrl}/${call.callId}/end`, {}));
    } catch {
      // Même raison que decline() : raccrocher localement ne doit jamais
      // rester bloqué par un échec réseau du côté serveur.
    } finally {
      this.finalizeEndedCall();
      this.busy = false;
    }
  }

  /**
   * Ajoute un participant à l'appel actif — la salle LiveKit accepte déjà
   * plusieurs participants nativement, seule la signalisation applicative
   * manquait côté serveur (voir calls.service.ts `inviteToCall`). Ne
   * modifie pas l'état local : l'invité rejoint via son propre `answer()`,
   * suivi comme n'importe quel autre appel entrant de son côté.
   */
  async invite(userId: number, name: string): Promise<void> {
    const call = this._activeCall();
    if (!call) return;
    await firstValueFrom(
      this.http.post(`${this.callsApiUrl}/${call.callId}/invite`, { userId }),
    );
    this._activeCall.update((c) => (c ? { ...c, invitedNames: [...c.invitedNames, name] } : c));
  }

  async getHistory(status: 'all' | 'missed' = 'all'): Promise<CallLogEntry[]> {
    return firstValueFrom(
      this.http.get<CallLogEntry[]>(`${this.callsApiUrl}/history`, { params: { status } }),
    );
  }

  minimize(): void {
    this._activeCall.update((c) => (c && c.phase === 'active' ? { ...c, phase: 'minimized' } : c));
  }

  expand(): void {
    this._activeCall.update((c) => (c && c.phase === 'minimized' ? { ...c, phase: 'active' } : c));
  }
}
